/* Ayudate · Panel de accesibilidad */

import React, { useState, useEffect } from "react";

export function FiltrosColor() {
  const M = {
    protanopia: "1 0 0 0 0  -0.2549 1.2549 0 0 0  0.3031 -0.5451 1.242 0 0  0 0 0 1 0",
    deuteranopia: "1 0 0 0 0  -0.4375 1.4375 0 0 0  0.2625 -0.5625 1.3 0 0  0 0 0 1 0",
    tritanopia: "1 0 0 0 0  0.035 1.532 -0.567 0 0  0.035 -0.51 1.475 0 0  0 0 0 1 0",
    grises: "0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 1 0",
  };
  return (
    <svg aria-hidden="true" focusable="false"
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
      <defs>
        {Object.entries(M).map(([id, v]) => (
          <filter key={id} id={`ay-${id}`} colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values={v} />
          </filter>
        ))}
      </defs>
    </svg>
  );
}

const ZOOMS = [70, 80, 90, 100, 110, 125, 150, 175];
const FILTROS = [
  { id: "ninguno", lb: "Sin filtro", d: "Colores originales" },
  { id: "protanopia", lb: "Protanopia", d: "Dificultad con el rojo" },
  { id: "deuteranopia", lb: "Deuteranopia", d: "Dificultad con el verde" },
  { id: "tritanopia", lb: "Tritanopia", d: "Dificultad con el azul" },
  { id: "grises", lb: "Escala de grises", d: "Sin color, solo contraste" }];

/* Se guarda en sessionStorage, no en un estado del componente: la persona
   navega entre "/", "/tutores", "/preguntas", etc. sin que la app use
   react-router, así que este componente se desmonta y se vuelve a montar en
   cada cambio de ruta. Sin esto, el aviso volvería a aparecer cada vez que
   vuelve a la página principal, aunque ya lo haya visto en esta sesión. */
const CLAVE_AVISO_VISTO = "ayudate_a11y_aviso_visto";

function avisoYaVisto() {
  try {
    return sessionStorage.getItem(CLAVE_AVISO_VISTO) === "1";
  } catch {
    return false;   // modo privado o cuota llena: se muestra cada vez, sin romper nada
  }
}

export function Accesibilidad({ ajustes, setAjustes }) {
  const [abierto, setAbierto] = useState(false);
  const [anuncio, setAnuncio] = useState("");
  /* La primera vez el botón se presenta con palabras: el símbolo universal
     de accesibilidad no lo reconoce todo el mundo. Solo la primerísima vez
     por sesión (ver avisoYaVisto arriba). */
  const [presentado, setPresentado] = useState(avisoYaVisto);

  const marcarVisto = () => {
    setPresentado(true);
    try { sessionStorage.setItem(CLAVE_AVISO_VISTO, "1"); } catch { /* seguir sin persistir */ }
  };

  useEffect(() => {
    if (presentado) return undefined;
    const t = setTimeout(marcarVisto, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentado]);
  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const set = (k, v, texto) => {
    setAjustes((a) => ({ ...a, [k]: v }));
    if (texto) setAnuncio(texto);
  };

  const restablecer = () => {
    setAjustes({ zoom: 100, fuente: false, espaciado: false, filtro: "ninguno",
      contraste: false, enlaces: false, movimiento: false });
    setAnuncio("Ajustes de accesibilidad restablecidos");
  };

  useEffect(() => {
    if (!abierto) return;
    const esc = (e) => {
      if (e.key === "Escape") { setAbierto(false); btnRef.current?.focus(); }
    };
    const fuera = (e) => {
      if (!e.target.closest(".a11y-panel") && !e.target.closest(".a11y-btn")) setAbierto(false);
    };
    window.addEventListener("keydown", esc);
    document.addEventListener("mousedown", fuera);
    panelRef.current?.querySelector("button, [href], input")?.focus();
    return () => {
      window.removeEventListener("keydown", esc);
      document.removeEventListener("mousedown", fuera);
    };
  }, [abierto]);

  const activos = (ajustes.zoom !== 100 ? 1 : 0) + (ajustes.fuente ? 1 : 0)
    + (ajustes.espaciado ? 1 : 0) + (ajustes.filtro !== "ninguno" ? 1 : 0)
    + (ajustes.contraste ? 1 : 0) + (ajustes.enlaces ? 1 : 0) + (ajustes.movimiento ? 1 : 0);

  const Interruptor = ({ id, lb, d, val, k }) => (
    <button type="button" className={`a11y-sw ${val ? "on" : ""}`} id={id}
      role="switch" aria-checked={val}
      onClick={() => set(k, !val, `${lb} ${!val ? "activado" : "desactivado"}`)}>
      <span className="a11y-swt"><i /></span>
      <span className="a11y-swl"><b>{lb}</b><span>{d}</span></span>
    </button>
  );

  return (
    <>
      {!presentado && !abierto && (
        <div className="a11y-tip" role="note">
          <b>¿Le cuesta leer la página?</b>
          <span>Toque acá para agrandar el texto, cambiar los colores o mejorar el contraste.</span>
          <button className="a11y-tipx" onClick={marcarVisto}
            aria-label="Ocultar este aviso">Entendido</button>
        </div>
      )}

      <button ref={btnRef} className={`a11y-btn ${!presentado && !abierto ? "llamando" : ""}`}
        aria-expanded={abierto} aria-controls="a11y-panel"
        aria-label={`Opciones de accesibilidad: agrandar texto, colores y contraste${activos ? `. ${activos} activas` : ""}`}
        title="Accesibilidad · agrandar texto y ajustar colores"
        onClick={() => { marcarVisto(); setAbierto((v) => !v); }}>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="10.4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="12" cy="6.4" r="1.75" fill="currentColor" />
          <path d="M5.6 9.1c2.1.7 4.2 1.05 6.4 1.05s4.3-.35 6.4-1.05"
            stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" fill="none" />
          <path d="M12 10.15v4.1m0 0-2.5 5.1m2.5-5.1 2.5 5.1"
            stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" fill="none" />
        </svg>
        {activos > 0 && <span className="a11y-badge" aria-hidden="true">{activos}</span>}
      </button>

      <div className="sr-only" role="status" aria-live="polite">{anuncio}</div>

      {abierto && (
        <div className="a11y-panel" id="a11y-panel" ref={panelRef} role="dialog"
          aria-modal="false" aria-label="Opciones de accesibilidad">
          <div className="a11y-h">
            <div>
              <h4>Ver mejor la página</h4>
              <p className="a11y-sub">Los cambios se aplican al instante.</p>
            </div>
            <button className="x" onClick={() => { setAbierto(false); btnRef.current?.focus(); }}
              aria-label="Cerrar el panel de accesibilidad">×</button>
          </div>

          <div className="a11y-b">
            {/* ---- Zoom ---- */}
            <div className="a11y-grp">
              <span className="a11y-lb" id="a11y-zoom-lb">Tamaño de la página</span>
              <div className="a11y-seg" role="group" aria-labelledby="a11y-zoom-lb">
                {ZOOMS.map((z) => (
                  <button type="button" key={z} aria-pressed={ajustes.zoom === z}
                    onClick={() => set("zoom", z, `Tamaño de la página ${z} por ciento`)}>
                    {z}%
                  </button>
                ))}
              </div>
              <p className="a11y-nota">Agranda o reduce todo el contenido, texto e imágenes por igual.</p>
            </div>

            {/* ---- Filtro de color ---- */}
            <div className="a11y-grp">
              <span className="a11y-lb" id="a11y-filtro-lb">Visión del color</span>
              <div className="a11y-list" role="group" aria-labelledby="a11y-filtro-lb">
                {FILTROS.map((f) => (
                  <button type="button" key={f.id} className={`a11y-op ${ajustes.filtro === f.id ? "on" : ""}`}
                    aria-pressed={ajustes.filtro === f.id}
                    onClick={() => set("filtro", f.id, `Filtro de color: ${f.lb}`)}>
                    <b>{f.lb}</b><span>{f.d}</span>
                  </button>
                ))}
              </div>
              <p className="a11y-nota">
                <b>No simula la deficiencia</b>: reajusta los colores para que se distingan mejor.
              </p>
            </div>

            {/* ---- Interruptores ---- */}
            <div className="a11y-grp">
              <span className="a11y-lb">Lectura</span>
              <Interruptor k="fuente" val={ajustes.fuente} lb="Fuente de alta legibilidad"
                d="Letras más diferenciables entre sí" />
              <Interruptor k="espaciado" val={ajustes.espaciado} lb="Más espacio entre líneas"
                d="Más aire entre líneas y palabras, para leer más cómodo" />
              <Interruptor k="contraste" val={ajustes.contraste} lb="Contraste reforzado"
                d="Texto y bordes más marcados" />
              <Interruptor k="enlaces" val={ajustes.enlaces} lb="Marcar enlaces y botones"
                d="Se subrayan y enmarcan, no dependen solo del color" />
              <Interruptor k="movimiento" val={ajustes.movimiento} lb="Reducir movimiento"
                d="Detiene animaciones y desplazamientos suaves" />
            </div>

            <button className="btn" style={{ width: "100%" }} onClick={restablecer}>
              Restablecer todo
            </button>
            <p className="a11y-nota" style={{ marginTop: 10 }}>
              Los ajustes duran mientras la pestaña esté abierta.
            </p>
          </div>
        </div>
      )}
    </>
  );
}


/* ------------------------------------------------------------------
   Políticas en una ventana propia: se abre encima del formulario
   sin cerrarlo, así no se pierde nada de lo que ya se llenó.
------------------------------------------------------------------- */
